import * as Yup from 'yup';
import type { AppDetailLayoutWithIdProps, AppDetailLayoutProps, AppDetailLayoutFoundProps } from './types';
import { Outlet, useParams, useLocation } from 'react-router';
import {
  getAssociatedResourceTypeDisplayName,
  getResourceTypeDisplayName,
  getTargetResource,
  isSamlApp,
} from '../../../lib/model';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { graphQueries } from '../../../api/graph.api';
import { appQueries, useUpsertAppSettings } from '../../../api/app.api';
import { AppSettingsDto, ResourceType } from '../../../api/app.types';
import { AppBaseDto, AppRegistration, EnterpriseApplication } from '../../../api/graph.types';
import { Formik, FormikHelpers } from 'formik';
import { faExternalLink, faLink } from '@fortawesome/free-solid-svg-icons';
import {
  HeaderButtonType,
  HeaderProps,
  LayoutMode,
  MainContainerProps,
  MenuProps,
  ItemNotFound,
  SecondaryTemplate,
  LayoutWidth,
  PolicyConstants,
  ReinitializeWhenPristine,
} from '@easylife365/react';
import {
  useCachedResource,
  useCredentialInfo,
  useHandleHttpError,
  useLicenseInfo,
  useOwnerCompliance,
  useShowEntraIdLinks,
} from '../../../lib/hooks';
import { AddCertificatePanelProvider, AddPasswordPanelProvider } from '../../panels';
import { AppDetailContextProvider } from '../../../lib/detail';
import { useMemo } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

// Supported URI schemes for help links
// These schemes work in both browsers and Microsoft Teams when the respective apps are installed
const SUPPORTED_URI_SCHEMES = [
  'http:',
  'https:',
  'mailto:',
  'tel:',
  'onenote:',
  'msteams:',
  'ms-word:',
  'ms-excel:',
  'ms-powerpoint:',
  'ms-access:',
  'ms-visio:',
] as const;

/**
 * Custom Yup validation method for URI validation
 * Validates that a string is either a valid URL or a supported URI scheme
 */
const createUriValidation = (t: TFunction<'appDetailLayout'>) =>
  Yup.string().test('is-valid-uri', t('validation.invalidUri'), function (value) {
    // Empty values are valid (field is optional)
    if (!value || value.trim() === '') {
      return true;
    }

    const normalizedValue = value.toLowerCase().trim();

    // Check if it starts with a supported URI scheme
    const hasValidScheme = SUPPORTED_URI_SCHEMES.some((scheme) => normalizedValue.startsWith(scheme));

    if (!hasValidScheme) {
      return false;
    }

    // For http/https, also validate the URL structure
    if (normalizedValue.startsWith('http:') || normalizedValue.startsWith('https:')) {
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }

    // For other supported schemes, the scheme check is sufficient
    return true;
  });

export const createValidationSchema = (t: TFunction<'appDetailLayout'>) => {
  const uriValidation = createUriValidation(t);

  return Yup.object({
    notificationSettings: Yup.object({
      isEnabled: Yup.boolean(),
      snoozingJustification: Yup.string().when(['isEnabled'], (values, schema) =>
        values[0] === false ? schema.requiredText() : schema
      ),
    }),
    helpLinks: Yup.object({
      overviewLink: uriValidation,
      certificatesLink: uriValidation,
      secretsLink: uriValidation,
      ownersLink: uriValidation,
      rulesLink: uriValidation,
      settingsLink: uriValidation,
    }),
    // Clearing the editor yields null; the notes are optional, so null must validate.
    notes: Yup.string().nullable().max(5000),
  }).nullable();
};

const AppDetailLayout = ({ resourceType }: AppDetailLayoutProps) => {
  const params = useParams();
  const id = params.id;

  return id ? <AppDetailLayoutWithId resourceType={resourceType} id={id} /> : <ItemNotFound />;
};

const AppDetailLayoutWithId: React.FC<AppDetailLayoutWithIdProps> = ({ id, resourceType }) => {
  const { data: app } = useSuspenseQuery({
    ...graphQueries.getResource(resourceType, id),
    initialData: useCachedResource(resourceType, id),
  });

  return app ? <AppDetailLayoutFound app={app} resourceType={resourceType} /> : <ItemNotFound />;
};

const AppDetailLayoutFound: React.FC<AppDetailLayoutFoundProps> = ({ app, resourceType }) => {
  const { t } = useTranslation('appDetailLayout');
  // Also subscribes the layout to language changes for the display names the model helpers produce.
  useTranslation('common');
  const validationSchema = useMemo(() => createValidationSchema(t), [t]);
  const location = useLocation();
  const targetResource = getTargetResource(resourceType);
  const isSaml = isSamlApp(app);
  const targetLink = getTargetLink(app);
  const baseUrl = `${getTargetResource(resourceType)}/${app.id}`;
  const showEntraIdLinks = useShowEntraIdLinks();
  const { isProfessional } = useLicenseInfo();
  const handleHttpError = useHandleHttpError();

  const [{ data: settings }, { data: associatedResource }, { data: permissions }, { data: policyBundle }] =
    useSuspenseQueries({
      queries: [
        appQueries.getAppSettings(app.id),
        graphQueries.getAssociatedResource(resourceType, app.appId),
        appQueries.getPermissions(resourceType, app.id),
        appQueries.getPolicyBundle(resourceType, app.id),
      ],
    });

  const helpLink = getHelpLink(location.pathname, settings);
  const associatedResourceLink = associatedResource && getAssociatedResourceLink(resourceType, associatedResource);

  const { mutateAsync: upsertAppSettings } = useUpsertAppSettings();

  const credentialInfo = useCredentialInfo(app, isSaml);
  const { certificates, secrets, expiredCertificates, expiredSecrets, expiringCertificates, expiringSecrets } =
    credentialInfo;

  const hasProblematicCertificates = expiredCertificates.length > 0 || expiringCertificates.length > 0;
  const hasProblematicSecrets = expiredSecrets.length > 0 || expiringSecrets.length > 0;

  const ownerCompliance = useOwnerCompliance(app, settings, policyBundle);

  const assignmentRequiredPolicyIsSet = policyBundle?.baselineConfigurationPolicy?.assignmentRequired != null;
  const accountEnabledPolicyIsSet = policyBundle?.baselineConfigurationPolicy?.accountEnabled != null;

  // Determine violations based on actual app properties vs policy requirements
  const assignmentRequiredViolation =
    assignmentRequiredPolicyIsSet &&
    app.appRoleAssignmentRequired !== policyBundle?.baselineConfigurationPolicy?.assignmentRequired;
  const accountEnabledViolation =
    accountEnabledPolicyIsSet && app.accountEnabled !== policyBundle?.baselineConfigurationPolicy?.accountEnabled;

  const isInactivityPolicyIncompliant = settings.policy.inSta === PolicyConstants.InCompliantState;
  const isBaselineConfigurationPolicyIncompliant = assignmentRequiredViolation || accountEnabledViolation;

  const isIncompliant =
    ownerCompliance.application.isBelowMinimum ||
    ownerCompliance.technical.isBelowMinimum ||
    ownerCompliance.business.isBelowMinimum ||
    (isProfessional && (isInactivityPolicyIncompliant || isBaselineConfigurationPolicyIncompliant));

  const hasUserPolicy =
    !!policyBundle?.minOwnersPolicy ||
    !!policyBundle?.minTechnicalOwnersPolicy ||
    !!policyBundle?.minBusinessOwnersPolicy ||
    (isProfessional && (!!policyBundle?.inactivityPolicy || !!policyBundle?.baselineConfigurationPolicy));

  const onSubmit = async (values: Readonly<AppSettingsDto>, submitProps: FormikHelpers<AppSettingsDto>) => {
    try {
      const updatedValues: AppSettingsDto = { ...values, appType: resourceType };
      const settings = await upsertAppSettings({
        id: app.id,
        request: updatedValues,
      });
      // isPaidLicense && permissions.initialize(details.app.id!, resourceType); // Why???
      submitProps.resetForm({ values: settings });
    } catch (error) {
      handleHttpError(error);
    }
  };

  const header: HeaderProps = {
    backButton: {
      navigateTo: `/${targetResource}`,
    },
    text: app.displayName ?? '',
    description: getResourceTypeDisplayName(resourceType),
    showIcon: false,
    primaryButton: showEntraIdLinks
      ? {
          text: 'Entra ID',
          icon: faExternalLink,
          to: targetLink,
          target: '_blank',
        }
      : undefined,
    secondaryButton: associatedResourceLink
      ? {
          text: getAssociatedResourceTypeDisplayName(resourceType),
          to: associatedResourceLink,
          icon: faLink,
        }
      : undefined,
    helpButton: !helpLink
      ? undefined
      : {
          title: t('help'),
          onClick: () => window.open(helpLink, '_blank'),
        },
  };

  const uniqueOwners = Array.from(
    new Set([
      ...app.owners.map((owner) => owner.id),
      ...settings.technicalOwnershipSettings.owners.map((technicalOwner) => technicalOwner.id),
      ...settings.businessOwnershipSettings.owners.map((businessOwner) => businessOwner.id),
    ])
  );

  return (
    <AppDetailContextProvider
      app={app}
      isSaml={isSaml}
      credentialInfo={credentialInfo}
      baseUrl={baseUrl}
      policyBundle={policyBundle}
      settings={settings}
      associatedResource={associatedResource}
      permissions={permissions}
    >
      <AddCertificatePanelProvider>
        <AddPasswordPanelProvider>
          <Formik initialValues={settings} onSubmit={onSubmit} validateOnMount validationSchema={validationSchema}>
            {({ handleSubmit, errors }) => {
              const menu: MenuProps = {
                categories: [
                  {
                    items: [
                      {
                        label: t('overview.label'),
                        navigateTo: `${baseUrl}/overview`,
                        page: {
                          header: {
                            text: t('overview.label'),
                            description: t('overview.description'),
                            primaryButton: {
                              text: t('buttons.save'),
                              type: HeaderButtonType.Submit,
                            },
                          },
                        },
                      },
                      ...(resourceType === ResourceType.AppRegistration || isSaml
                        ? [
                            {
                              label: `${isSaml ? t('samlPrefixed', { label: t('certificates.label') }) : t('certificates.label')} (${certificates.length})`,
                              navigateTo: `${baseUrl}/certificates`,
                              warning: hasProblematicCertificates ? t('certificates.warning') : '',
                              page: {
                                header: {
                                  text: t('certificates.label'),
                                  description: t('certificates.description'),
                                },
                                layoutWidth: LayoutWidth.FULL,
                              },
                            },
                          ]
                        : []),
                      ...(resourceType === ResourceType.AppRegistration
                        ? [
                            {
                              label: `${t('secrets.label')} (${secrets.length})`,
                              navigateTo: `${baseUrl}/secrets`,
                              warning: hasProblematicSecrets ? t('secrets.warning') : '',
                              page: {
                                header: {
                                  text: t('secrets.label'),
                                  description: t('secrets.description'),
                                },
                                layoutWidth: LayoutWidth.FULL,
                              },
                            },
                          ]
                        : []),
                      {
                        label: `${t('owners.label')} (${uniqueOwners.length})`,
                        navigateTo: `${baseUrl}/owners`,
                        page: {
                          header: {
                            text: t('owners.label'),
                            description: t('owners.description'),
                          },
                          layoutMode: LayoutMode.LIST,
                        },
                      },
                      {
                        label: t('settings.label'),
                        warning:
                          errors.notificationSettings ||
                          errors.technicalOwnershipSettings ||
                          errors.businessOwnershipSettings
                            ? t('settings.warning')
                            : '',
                        navigateTo: `${baseUrl}/settings`,
                        noSwitch: true,
                        page: {
                          header: {
                            text: t('settings.label'),
                            description: t('settings.description'),
                            primaryButton: {
                              text: t('buttons.save'),
                              type: HeaderButtonType.Submit,
                            },
                            secondaryButton: {
                              text: t('buttons.discardChanges'),
                              type: HeaderButtonType.Cancel,
                            },
                          },
                        },
                      },
                      // The Security Center closes the menu, as it does in Collab: it reports on the
                      // resource the preceding pages configure. Its Security Cards stack in the narrow
                      // centred column of the detail pages, as in Collab and Mail.
                      ...(hasUserPolicy
                        ? [
                            {
                              label: t('rules.label'),
                              navigateTo: `${baseUrl}/policies`,
                              warning: isIncompliant ? t('rules.warning') : '',
                              noSwitch: true,
                              page: {
                                header: {
                                  text: t('rules.label'),
                                  description: t('rules.description'),
                                },
                              },
                            },
                          ]
                        : []),
                    ],
                  },
                ],
              };

              const mainContainer: MainContainerProps = {
                menu: menu,
              };

              return (
                <>
                  <ReinitializeWhenPristine entity={settings} entityKey={app.id} />
                  <SecondaryTemplate header={header} mainContainer={mainContainer} handleSubmit={handleSubmit}>
                    <Outlet />
                  </SecondaryTemplate>
                </>
              );
            }}
          </Formik>
        </AddPasswordPanelProvider>
      </AddCertificatePanelProvider>
    </AppDetailContextProvider>
  );
};

function getHelpLink(pathname: string, settings: AppSettingsDto) {
  if (pathname.endsWith('overview')) return settings.helpLinks.overviewLink;

  if (pathname.endsWith('certificates')) return settings.helpLinks.certificatesLink;

  if (pathname.endsWith('secrets')) return settings.helpLinks.secretsLink;

  if (pathname.endsWith('owners')) return settings.helpLinks.ownersLink;

  if (pathname.endsWith('policies')) return settings.helpLinks.rulesLink;

  if (pathname.endsWith('settings')) return settings.helpLinks.settingsLink;
}

function getTargetLink(app: AppBaseDto) {
  switch (app.resourceType) {
    case ResourceType.AppRegistration:
      return `https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Overview/appId/${app.appId}/isMSAApp~/false`;

    case ResourceType.EnterpriseApplication:
      return `https://entra.microsoft.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Overview/objectId/${app.id}/appId/${app.appId}/preferredSingleSignOnMode~/null/servicePrincipalType/Application/fromNav/`;
  }
}

function getAssociatedResourceLink(
  resourceType: ResourceType,
  associatedResource: AppRegistration | EnterpriseApplication
) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return `/apps/${associatedResource.id}/overview`;

    case ResourceType.EnterpriseApplication:
      return `/registeredApps/${associatedResource.id}/overview`;
  }
}

export default AppDetailLayout;
